import React, { useState } from 'react';
import { Star } from 'lucide-react';

interface V1StarRatingProps {
  value: number;
  onChange: (value: number) => void;
  maxStars?: number;
  starSize?: number;
}

export const V1StarRating: React.FC<V1StarRatingProps> = ({
  value,
  onChange,
  maxStars = 5,
  starSize = 32,
}) => {
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);

  const handleStarClick = (starNumber: number) => {
    // Toggle off if clicking the same star
    onChange(value === starNumber ? 0 : starNumber);
  };

  const handleStarHover = (starNumber: number) => {
    setHoveredStar(starNumber);
  };

  const handleMouseLeave = () => {
    setHoveredStar(null);
  };

  const getStarFillColor = (starNumber: number) => {
    const effectiveValue = hoveredStar !== null ? hoveredStar : value;
    return starNumber <= effectiveValue ? '#FBBF24' : 'transparent'; // yellow-400
  };

  const getStarStrokeColor = (starNumber: number) => {
    const effectiveValue = hoveredStar !== null ? hoveredStar : value;
    return starNumber <= effectiveValue ? '#FBBF24' : '#D1D5DB'; // yellow-400 : gray-300
  };

  return (
    <div 
      className="inline-flex items-center gap-1"
      onMouseLeave={handleMouseLeave}
    >
      {Array.from({ length: maxStars }, (_, index) => {
        const starNumber = index + 1;
        return (
          <button
            key={starNumber}
            type="button"
            onClick={() => handleStarClick(starNumber)}
            onMouseEnter={() => handleStarHover(starNumber)}
            className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded p-1 transition-transform hover:scale-110"
            aria-label={`Rate ${starNumber} out of ${maxStars} stars`}
          >
            <Star
              size={starSize}
              fill={getStarFillColor(starNumber)}
              stroke={getStarStrokeColor(starNumber)}
              strokeWidth={2}
              className="transition-all duration-200"
            />
          </button>
        );
      })}
      
      {value > 0 && (
        <span className="ml-2 text-sm text-gray-600">
          ({value} star{value !== 1 ? 's' : ''})
        </span>
      )}
    </div>
  );
};